import React from 'react';
import { X, Stethoscope, User, Calendar, Clock, Heart, Activity, Thermometer } from 'lucide-react';

const ConsultationDetailsModal = ({ consultation, isOpen, onClose }) => {
  if (!isOpen || !consultation) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-cyan-100 rounded-full">
              <Stethoscope className="h-6 w-6 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Détails de la consultation</h2>
              <p className="text-gray-600">{consultation.patient?.user?.name || 'Patient inconnu'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Informations générales */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Informations générales</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center text-sm text-gray-600">
                <User className="h-4 w-4 text-gray-400 mr-2" />
                <span><strong>Patient:</strong> {consultation.patient?.user?.name || 'Non spécifié'}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Stethoscope className="h-4 w-4 text-gray-400 mr-2" />
                <span><strong>Médecin:</strong> Dr. {consultation.doctor?.user?.name || 'Non spécifié'}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <span><strong>Date:</strong> {new Date(consultation.consultationDate).toLocaleDateString('fr-FR')}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                <span><strong>Heure:</strong> {new Date(consultation.consultationDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              {consultation.hospital && (
                <div className="flex items-center text-sm text-gray-600 col-span-2">
                  <span><strong>Hôpital:</strong> {consultation.hospital.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Motif et diagnostic */}
          {(consultation.chiefComplaint || consultation.diagnosis) && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Consultation médicale</h3>
              {consultation.chiefComplaint && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-700 mb-1">Motif de consultation:</p>
                  <p className="text-sm text-gray-600">{consultation.chiefComplaint}</p>
                </div>
              )}
              {consultation.diagnosis && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Diagnostic:</p>
                  <p className="text-sm text-gray-600">{consultation.diagnosis}</p>
                </div>
              )}
            </div>
          )}

          {/* Signes vitaux */}
          {(consultation.bloodPressure || consultation.heartRate || consultation.temperature || consultation.weight) && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Signes vitaux</h3>
              <div className="grid grid-cols-2 gap-4">
                {consultation.bloodPressure && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Activity className="h-4 w-4 text-gray-400 mr-2" />
                    <span>Tension: {consultation.bloodPressure}</span>
                  </div>
                )}
                {consultation.heartRate && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Heart className="h-4 w-4 text-gray-400 mr-2" />
                    <span>Rythme cardiaque: {consultation.heartRate} BPM</span>
                  </div>
                )}
                {consultation.temperature && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Thermometer className="h-4 w-4 text-gray-400 mr-2" />
                    <span>Température: {consultation.temperature}°C</span>
                  </div>
                )}
                {consultation.weight && (
                  <div className="flex items-center text-sm text-gray-600">
                    <span>Poids: {consultation.weight} kg</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Statut et priorité */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Statut</h3>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                consultation.status === 'Terminée' ? 'bg-green-100 text-green-800' :
                consultation.status === 'En cours' ? 'bg-blue-100 text-blue-800' :
                consultation.status === 'En attente' ? 'bg-yellow-100 text-yellow-800' :
                consultation.status === 'Annulée' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {consultation.status}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                consultation.priority === 'Urgente' ? 'bg-red-100 text-red-800' :
                consultation.priority === 'Élevée' ? 'bg-orange-100 text-orange-800' :
                consultation.priority === 'Normale' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {consultation.priority}
              </span>
              {consultation.followUpRequired && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  Suivi requis
                </span>
              )}
            </div>
          </div>

          {/* Notes */}
          {consultation.notes && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
              <p className="text-sm text-gray-600">{consultation.notes}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsultationDetailsModal;
